import StringsStore from "sh-strings/store";

import { getSharedContext } from "@microsoft/shifts-experiences-shared";
import { ThemeType } from "@microsoft/shifts-ui-core";
import {
    teamsDarkTheme,
    teamsHighContrastTheme,
    teamsLightTheme,
    Theme as FluentTheme
} from '@fluentui/react-components';
import ECSConfigService from "sh-services/lib/ecsconfigservice/ECSConfigService";
import ECSConfigKey from "sh-services/lib/ecsconfigservice/ECSConfigKey";

export type Theme = "themePink" | "themeDarkPink" | "themeDarkRed" | "themeCranberry" | "themeDarkOrange" | "themeBronze" | "themePeach" | "themeDarkYellow" | "themeYellow" | "themeGold" | "themeLime" | "themeForest" | "themeGreen" | "themeLightGreen" | "themeJade" | "themeDarkGreen" | "themeLightTeal" | "themeDarkTeal" | "themeSteel" | "themeSkyBlue" | "themeBlue" | "themeDarkBlue" | "themeBlueGray" | "themeLavender" | "themePurple" | "themeDarkPurple" | "themeLilac" | "themePlum" | "themeMagenta" | "themeDarkBrown" | "themeBeige" | "themeCharcoal" | "themeGray" | "themeSilver" | "themeWhite" | "themeTimeOffGray";
export const Themes = {
    /**
     * Note - some themes have been mapped to new colors in the V2 scheduler. The "themeXXXX" values are stored on service and should not be changed.
     * Colors are ordered 1-35 per the spec spreadsheet.
     */
    // 1. Pink
    Pink: "themePink" as Theme,
    // 2. Dark pink
    DarkPink: "themeDarkPink" as Theme,
    // 3. Dark red (new)
    DarkRed: "themeDarkRed" as Theme,
    // 4. Cranberry (new)
    Cranberry: "themeCranberry" as Theme,
    // 5. Dark orange (new)
    DarkOrange: "themeDarkOrange" as Theme,
    // 6. Bronze (new)
    Bronze: "themeBronze" as Theme,
    // 7. Peach (new)
    Peach: "themePeach" as Theme,
    // 8. Dark yellow
    DarkYellow: "themeDarkYellow" as Theme,
    // 9. Yellow
    Yellow: "themeYellow" as Theme,
    // 10. Gold (new)
    Gold: "themeGold" as Theme,
    // 11. Lime (re-enabled)
    Lime: "themeLime" as Theme,
    // 12. Forest (new)
    Forest: "themeForest" as Theme,
    // 13. Green
    Green: "themeGreen" as Theme,
    // 14. Light green (new)
    LightGreen: "themeLightGreen" as Theme,
    // 15. Jade (new)
    Jade: "themeJade" as Theme,
    // 16. Dark green
    DarkGreen: "themeDarkGreen" as Theme,
    // 17. Light teal (new)
    LightTeal: "themeLightTeal" as Theme,
    // 18. Dark teal (new)
    DarkTeal: "themeDarkTeal" as Theme,
    // 19. Steel (new)
    Steel: "themeSteel" as Theme,
    // 20. Sky blue (new)
    SkyBlue: "themeSkyBlue" as Theme,
    // 21. Blue
    Blue: "themeBlue" as Theme,
    // 22. Dark blue
    DarkBlue: "themeDarkBlue" as Theme,
    // 23. Blue gray (new)
    BlueGray: "themeBlueGray" as Theme,
    // 24. Lavender (new)
    Lavender: "themeLavender" as Theme,
    // 25. Purple
    Purple: "themePurple" as Theme,
    // 26. Dark purple
    DarkPurple: "themeDarkPurple" as Theme,
    // 27. Lilac (new)
    Lilac: "themeLilac" as Theme,
    // 28. Plum (new)
    Plum: "themePlum" as Theme,
    // 29. Magenta (new)
    Magenta: "themeMagenta" as Theme,
    // 30. Dark brown (new)
    DarkBrown: "themeDarkBrown" as Theme,
    // 31. Beige (new)
    Beige: "themeBeige" as Theme,
    // 32. Charcoal (new)
    Charcoal: "themeCharcoal" as Theme,
    // 33. Gray
    Gray: "themeGray" as Theme,
    // 34. Silver (new)
    Silver: "themeSilver" as Theme,
    // 35. White
    White: "themeWhite" as Theme,
    // Special
    TimeOffGray: "themeTimeOffGray" as Theme
};

export const ThemeNames: Record<string, string> = {
    // Active colors (ordered 1-35)
    themePink: "pink",
    themeDarkPink: "darkpink",
    themeDarkRed: "darkred",
    themeCranberry: "cranberry",
    themeDarkOrange: "darkorange",
    themeBronze: "bronze",
    themePeach: "peach",
    themeDarkYellow: "darkyellow",
    themeYellow: "yellow",
    themeGold: "gold",
    themeLime: "lime",
    themeForest: "forest",
    themeGreen: "green",
    themeLightGreen: "lightgreen",
    themeJade: "jade",
    themeDarkGreen: "darkgreen",
    themeLightTeal: "lightteal",
    themeDarkTeal: "darkteal",
    themeSteel: "steel",
    themeSkyBlue: "skyblue",
    themeBlue: "blue",
    themeDarkBlue: "darkblue",
    themeBlueGray: "bluegray",
    themeLavender: "lavender",
    themePurple: "purple",
    themeDarkPurple: "darkpurple",
    themeLilac: "lilac",
    themePlum: "plum",
    themeMagenta: "magenta",
    themeDarkBrown: "darkbrown",
    themeBeige: "beige",
    themeCharcoal: "charcoal",
    themeGray: "gray",
    themeSilver: "silver",
    themeWhite: "white",
    themeTimeOffGray: "gray",
    // Deprecated aliases
    themeTeal: "lightteal",
    themeCyan: "steel",
    themeBrown: "darkbrown",
    themeMaroon: "darkred",
    themeNavy: "darkblue",
    themeCoral: "cranberry",
    themeSteelBlue: "bluegray",
    themeBurgundy: "darkred"
};

const themeBgColorMap: Record<string, string> = {
    themePink: "var(--theme-color-pink)",
    themeDarkPink: "var(--theme-color-dark-pink)",
    themeDarkRed: "var(--theme-color-darkred)",
    themeCranberry: "var(--theme-color-cranberry)",
    themeDarkOrange: "var(--theme-color-darkorange)",
    themeBronze: "var(--theme-color-bronze)",
    themePeach: "var(--theme-color-peach)",
    themeDarkYellow: "var(--theme-color-dark-yellow)",
    themeYellow: "var(--theme-color-yellow)",
    themeGold: "var(--theme-color-gold)",
    themeLime: "var(--theme-color-lime)",
    themeForest: "var(--theme-color-forest)",
    themeGreen: "var(--theme-color-green)",
    themeLightGreen: "var(--theme-color-lightgreen)",
    themeJade: "var(--theme-color-jade)",
    themeDarkGreen: "var(--theme-color-dark-green)",
    themeLightTeal: "var(--theme-color-lightteal)",
    themeDarkTeal: "var(--theme-color-darkteal)",
    themeSteel: "var(--theme-color-steel)",
    themeSkyBlue: "var(--theme-color-skyblue)",
    themeBlue: "var(--theme-color-blue)",
    themeDarkBlue: "var(--theme-color-dark-blue)",
    themeBlueGray: "var(--theme-color-bluegray)",
    themeLavender: "var(--theme-color-lavender)",
    themePurple: "var(--theme-color-purple)",
    themeDarkPurple: "var(--theme-color-dark-purple)",
    themeLilac: "var(--theme-color-lilac)",
    themePlum: "var(--theme-color-plum)",
    themeMagenta: "var(--theme-color-magenta)",
    themeDarkBrown: "var(--theme-color-darkbrown)",
    themeBeige: "var(--theme-color-beige)",
    themeCharcoal: "var(--theme-color-charcoal)",
    themeGray: "var(--theme-color-dark-gray)",
    themeSilver: "var(--theme-color-silver)",
    themeWhite: "var(--theme-color-white)",
    themeTimeOffGray: "var(--theme-color-dark-gray)"
};

const themeBarColorMap: Record<string, string> = {
    themePink: "var(--theme-color-bar-pink)",
    themeDarkPink: "var(--theme-color-dark-bar-pink)",
    themeDarkRed: "var(--theme-color-bar-darkred)",
    themeCranberry: "var(--theme-color-bar-cranberry)",
    themeDarkOrange: "var(--theme-color-bar-darkorange)",
    themeBronze: "var(--theme-color-bar-bronze)",
    themePeach: "var(--theme-color-bar-peach)",
    themeDarkYellow: "var(--theme-color-dark-bar-yellow)",
    themeYellow: "var(--theme-color-bar-yellow)",
    themeGold: "var(--theme-color-bar-gold)",
    themeLime: "var(--theme-color-bar-lime)",
    themeForest: "var(--theme-color-bar-forest)",
    themeGreen: "var(--theme-color-bar-green)",
    themeLightGreen: "var(--theme-color-bar-lightgreen)",
    themeJade: "var(--theme-color-bar-jade)",
    themeDarkGreen: "var(--theme-color-dark-bar-green)",
    themeLightTeal: "var(--theme-color-bar-lightteal)",
    themeDarkTeal: "var(--theme-color-bar-darkteal)",
    themeSteel: "var(--theme-color-bar-steel)",
    themeSkyBlue: "var(--theme-color-bar-skyblue)",
    themeBlue: "var(--theme-color-bar-blue)",
    themeDarkBlue: "var(--theme-color-dark-bar-blue)",
    themeBlueGray: "var(--theme-color-bar-bluegray)",
    themeLavender: "var(--theme-color-bar-lavender)",
    themePurple: "var(--theme-color-bar-purple)",
    themeDarkPurple: "var(--theme-color-dark-bar-purple)",
    themeLilac: "var(--theme-color-bar-lilac)",
    themePlum: "var(--theme-color-bar-plum)",
    themeMagenta: "var(--theme-color-bar-magenta)",
    themeDarkBrown: "var(--theme-color-bar-darkbrown)",
    themeBeige: "var(--theme-color-bar-beige)",
    themeCharcoal: "var(--theme-color-bar-charcoal)",
    themeGray: "var(--theme-color-dark-bar-gray)",
    themeSilver: "var(--theme-color-bar-silver)",
    themeWhite: "var(--theme-color-bar-white)",
    themeTimeOffGray: "var(--theme-color-dark-bar-gray)"
};

/**
 * Utilities for Themes
 */
export default class ThemeUtils {
    /*
     * Get Localized Themes map
     * @param (boolean) isTimeOff : indicates if themes should be mapped for Timeoffs
     */
    public static getLocalizedThemeNames(isTimeOff: boolean = false): Map<string, string> {
        let themeNames: Map<string, string> = new Map<string, string>();

        const themeStrings = StringsStore().registeredStringModules.get("themes").strings;
        const isColorsEnabled = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableColors);

        if (isColorsEnabled) {
            // Expanded palette: 35 colors ordered per spec
            themeNames.set(themeStrings.get("pink"), Themes.Pink);                 // 1
            themeNames.set(themeStrings.get("darkpink"), Themes.DarkPink);         // 2
            themeNames.set(themeStrings.get("darkred"), Themes.DarkRed);           // 3
            themeNames.set(themeStrings.get("cranberry"), Themes.Cranberry);       // 4
            themeNames.set(themeStrings.get("darkorange"), Themes.DarkOrange);     // 5
            themeNames.set(themeStrings.get("bronze"), Themes.Bronze);             // 6
            themeNames.set(themeStrings.get("peach"), Themes.Peach);               // 7
            themeNames.set(themeStrings.get("darkyellow"), Themes.DarkYellow);     // 8
            themeNames.set(themeStrings.get("yellow"), Themes.Yellow);             // 9
            themeNames.set(themeStrings.get("gold"), Themes.Gold);                 // 10
            themeNames.set(themeStrings.get("lime"), Themes.Lime);                 // 11
            themeNames.set(themeStrings.get("forest"), Themes.Forest);             // 12
            themeNames.set(themeStrings.get("green"), Themes.Green);               // 13
            themeNames.set(themeStrings.get("lightgreen"), Themes.LightGreen);     // 14
            themeNames.set(themeStrings.get("jade"), Themes.Jade);                 // 15
            themeNames.set(themeStrings.get("darkgreen"), Themes.DarkGreen);       // 16
            themeNames.set(themeStrings.get("lightteal"), Themes.LightTeal);       // 17
            themeNames.set(themeStrings.get("darkteal"), Themes.DarkTeal);         // 18
            themeNames.set(themeStrings.get("steel"), Themes.Steel);               // 19
            themeNames.set(themeStrings.get("skyblue"), Themes.SkyBlue);           // 20
            themeNames.set(themeStrings.get("blue"), Themes.Blue);                 // 21
            themeNames.set(themeStrings.get("darkblue"), Themes.DarkBlue);         // 22
            themeNames.set(themeStrings.get("bluegray"), Themes.BlueGray);         // 23
            themeNames.set(themeStrings.get("lavender"), Themes.Lavender);         // 24
            themeNames.set(themeStrings.get("purple"), Themes.Purple);             // 25
            themeNames.set(themeStrings.get("darkpurple"), Themes.DarkPurple);     // 26
            themeNames.set(themeStrings.get("lilac"), Themes.Lilac);               // 27
            themeNames.set(themeStrings.get("plum"), Themes.Plum);                 // 28
            themeNames.set(themeStrings.get("magenta"), Themes.Magenta);           // 29
            themeNames.set(themeStrings.get("darkbrown"), Themes.DarkBrown);       // 30
            themeNames.set(themeStrings.get("beige"), Themes.Beige);               // 31
            themeNames.set(themeStrings.get("charcoal"), Themes.Charcoal);         // 32
            themeNames.set(themeStrings.get("gray"), isTimeOff ? Themes.TimeOffGray : Themes.Gray); // 33
            themeNames.set(themeStrings.get("silver"), Themes.Silver);             // 34
            themeNames.set(themeStrings.get("white"), Themes.White);               // 35
        } else {
            // Original palette: 13 colors
            themeNames.set(themeStrings.get("white"), Themes.White);
            themeNames.set(themeStrings.get("blue"), Themes.Blue);
            themeNames.set(themeStrings.get("green"), Themes.Green);
            themeNames.set(themeStrings.get("purple"), Themes.Purple);
            themeNames.set(themeStrings.get("pink"), Themes.Pink);
            themeNames.set(themeStrings.get("yellow"), Themes.Yellow);
            themeNames.set(themeStrings.get("gray"), isTimeOff ? Themes.TimeOffGray : Themes.Gray);
            themeNames.set(themeStrings.get("darkblue"), Themes.DarkBlue);
            themeNames.set(themeStrings.get("darkgreen"), Themes.DarkGreen);
            themeNames.set(themeStrings.get("darkpurple"), Themes.DarkPurple);
            themeNames.set(themeStrings.get("darkpink"), Themes.DarkPink);
            themeNames.set(themeStrings.get("darkyellow"), Themes.DarkYellow);
        }

        return themeNames;
    }

    /**
     * Gets the CSS custom property for a theme's background color
     */
    public static getThemeBgColor(themeKey: string): string {
        return themeBgColorMap[themeKey] || "transparent";
    }

    /**
     * Gets the CSS custom property for a theme's bar/foreground color
     */
    public static getThemeBarColor(themeKey: string): string {
        return themeBarColorMap[themeKey] || "transparent";
    }

    public static getLocalizedThemeName(theme: string): string {
        return StringsStore().registeredStringModules.get("themes").strings.get(theme);
    }

     /**
     * Get className that represents theme for timeOffRequest shifts
     */
    public static getTimeOffRequestThemeClass() {
        return "themeTimeOffRequest";
    }

    /**
     * Default theme for work Shifts
     */
    public static shiftDefaultTheme = Themes.Blue;

    /**
     * Default theme for Open Shifts
     */
    public static openShiftDefaultTheme = Themes.Blue;

    /**
     * Default theme for TimeOffs
     */
    public static timeOffDefaultTheme = Themes.TimeOffGray;

    public static getDefaultShiftTheme(isTimeOff: boolean): string {
        return isTimeOff ? this.timeOffDefaultTheme : this.shiftDefaultTheme;
    }

    public static getValidShiftTheme(shiftTheme: string, isTimeOff: boolean): string {
        let resultTheme = shiftTheme;
        if (!shiftTheme || shiftTheme.indexOf("theme") < 0) {
            // Determine a default theme if missing or invalid theme value
            resultTheme = this.getDefaultShiftTheme(isTimeOff);
        } else {
            // Always map deprecated theme values to their new equivalents for back-compat
            switch (shiftTheme) {
                case "themeTeal":
                    resultTheme = Themes.LightTeal;
                    break;
                case "themeCyan":
                    resultTheme = Themes.Steel;
                    break;
                case "themeBrown":
                    resultTheme = Themes.DarkBrown;
                    break;
                case "themeMaroon":
                    resultTheme = Themes.DarkRed;
                    break;
                case "themeNavy":
                    resultTheme = Themes.DarkBlue;
                    break;
                case "themeCoral":
                    resultTheme = Themes.Cranberry;
                    break;
                case "themeSteelBlue":
                    resultTheme = Themes.BlueGray;
                    break;
                case "themeBurgundy":
                    resultTheme = Themes.DarkRed;
                    break;
            }

            // When expanded colors are off, fall back to default if the color isn't in the original palette
            if (!ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableColors)) {
                const originalThemes = [
                    Themes.Pink, Themes.DarkPink, Themes.DarkYellow, Themes.Yellow,
                    Themes.Green, Themes.DarkGreen, Themes.Blue, Themes.DarkBlue,
                    Themes.Purple, Themes.DarkPurple, Themes.Gray, Themes.White,
                    Themes.TimeOffGray
                ];
                if (originalThemes.indexOf(resultTheme as Theme) < 0) {
                    resultTheme = this.getDefaultShiftTheme(isTimeOff);
                }
            }
        }
        return resultTheme;
    }

    public static getShiftsViewSlotBarTheme(): string {
        return Themes.TimeOffGray;
    }

    public static getFluentV2ShiftTheme (): FluentTheme {
        const { theme } = getSharedContext();
        if (theme === ThemeType.Dark ) {
            return teamsDarkTheme;
        } else if (theme === ThemeType.HighContrast) {
            return teamsHighContrastTheme;
        } else {
            return teamsLightTheme;
        }
    }

}
